/**
 * Why a session was ended, handed from App.tsx to Auth.tsx over localStorage.
 *
 * `accountNotActivated` widened this beyond disabled accounts, so the names say
 * "rejected session" now. The three runtime strings deliberately did not move
 * with them: the storage key below is read by a page load that may already be
 * in flight across a deploy, and `auth_disabled_user_error` is what existing
 * Sentry filters group on. Renaming either buys nothing and breaks something.
 */
export const REJECTED_SESSION_STORAGE_KEY = 'disabledUserError';

export const REJECTED_SESSION_KEYS = {
	ACCOUNT_DISABLED: 'accountDisabled',
	NO_ORGANIZATION: 'noOrganization',
	ACCOUNT_NOT_ACTIVATED: 'accountNotActivated',
} as const;

export type RejectedSessionKey = (typeof REJECTED_SESSION_KEYS)[keyof typeof REJECTED_SESSION_KEYS];

/**
 * The message each reason shows on the login screen. Kept beside the keys, and
 * typed against them, so a new reason cannot be added without deciding what the
 * user is actually told.
 */
const REJECTED_SESSION_MESSAGES: Record<RejectedSessionKey, string> = {
	[REJECTED_SESSION_KEYS.ACCOUNT_DISABLED]: 'message.loginScreen.accountDisabled',
	[REJECTED_SESSION_KEYS.NO_ORGANIZATION]: 'message.loginScreen.noOrganization',
	[REJECTED_SESSION_KEYS.ACCOUNT_NOT_ACTIVATED]: 'message.loginScreen.accountNotActivated',
};

/**
 * Deliberately neutral: it is shown only for a value this build does not know,
 * so it must not assert a reason. The previous default claimed the account had
 * no organization, which for an unrecognised key is simply a guess.
 */
const UNKNOWN_REJECTION_MESSAGE = 'message.loginScreen.sessionEnded';

/**
 * Resolves whatever string came back out of storage. The argument is `string`
 * rather than `RejectedSessionKey` on purpose - an unrecognised value is the
 * expected case here (an older build wrote it, or someone edited storage), and
 * casting it into the key type would erase exactly the safety the map provides.
 */
export const rejectedSessionMessage = (key: string): string =>
	REJECTED_SESSION_MESSAGES[key as RejectedSessionKey] ?? UNKNOWN_REJECTION_MESSAGE;
